/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import clsx from 'clsx';
import {
  BarChart3, PieChart, Sparkles, MessagesSquare, MessageSquare, Phone, Inbox, Smartphone, Palette, TrendingUp, BookMarked, Search, BookOpen, Bot, ClipboardList, Compass, Mail, Hammer, Radar, Workflow, Briefcase, Clock, FileCog, Gavel, Hourglass, Landmark, Scale, Timer, CalendarDays, CheckSquare, FileText, FolderKanban, KeyRound, LogOut, Menu, ReceiptText, ScrollText, Settings, Users, Wallet, X,
} from 'lucide-react';
import { ReactNode, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useBranding } from '@/lib/branding';
import { useAuth } from '@/lib/auth';
import { Tour, startTour } from './Tour';
import { IncomingCallPopup } from '@/pages/Calls';

interface Item { to: string; label: string; icon: ReactNode; module?: string | string[]; perm?: string }

const NAV: { title?: string; items: Item[] }[] = [
  { items: [{ to: '/', label: 'Сводка', icon: <BarChart3 size={18} />, module: 'M10', perm: 'report.read' }, { to: '/bi', label: 'Дашборды', icon: <PieChart size={18} />, module: 'M42', perm: 'report.read' }] },
  {
    title: 'Работа',
    items: [
      { to: '/tasks', label: 'Задачи', icon: <CheckSquare size={18} />, module: 'M08' },
      { to: '/calendar', label: 'Календарь', icon: <CalendarDays size={18} />, module: 'M08' },
      { to: '/matters', label: 'Дела', icon: <Briefcase size={18} />, perm: 'matter.read' },
      { to: '/documents', label: 'Документы', icon: <FileText size={18} />, module: 'M09', perm: 'document.read' },
      { to: '/search', label: 'Поиск по смыслу', icon: <Search size={18} />, module: 'M31', perm: 'document.read' },
      { to: '/ezp', label: 'Заказные письма', icon: <Mail size={18} />, module: 'M13', perm: 'document.read' },
      { to: '/assistant', label: 'ИИ-ассистент', icon: <Bot size={18} />, module: 'M20' },
      { to: '/agent', label: 'ИИ-агент', icon: <Sparkles size={18} />, module: 'M32', perm: 'task.read' },
      { to: '/automations', label: 'Автоматизации', icon: <Workflow size={18} />, module: 'M37', perm: 'task.read' },
    ],
  },
  {
    title: 'Юридический контур',
    items: [
      { to: '/deadlines', label: 'Процессуальные сроки', icon: <Hourglass size={18} />, module: 'M40', perm: 'matter.read' },
      { to: '/court-cases', label: 'Судебные дела', icon: <Gavel size={18} />, module: 'M12', perm: 'matter.read' },
      { to: '/practice', label: 'Судебная практика', icon: <BookMarked size={18} />, module: 'M21', perm: 'matter.read' },
      { to: '/monitoring', label: 'Мониторинг', icon: <Radar size={18} />, module: 'M41', perm: 'party.read' },
      { to: '/bankruptcy', label: 'Банкротство', icon: <Landmark size={18} />, module: 'M14', perm: 'bankruptcy.read' },
      { to: '/auctions', label: 'Торги', icon: <Hammer size={18} />, module: 'M16', perm: 'bankruptcy.read' },
      { to: '/debts', label: 'Дебиторка', icon: <Scale size={18} />, module: 'M11', perm: 'debt.read' },
      { to: '/writs', label: 'Исполнительные листы', icon: <Clock size={18} />, module: 'M17', perm: 'writ.read' },
      { to: '/collection', label: 'Аналитика взыскания', icon: <TrendingUp size={18} />, module: ['M19', 'M23'], perm: 'debt.read' },
      { to: '/time', label: 'Время и счета', icon: <Timer size={18} />, module: 'M35', perm: 'time.write' },
    ],
  },
  {
    title: 'Клиенты и продажи',
    items: [
      { to: '/deals', label: 'Воронка', icon: <FolderKanban size={18} />, module: 'M02', perm: 'deal.read' },
      { to: '/forms', label: 'Онлайн-формы', icon: <ClipboardList size={18} />, module: 'M38', perm: 'deal.read' },
      { to: '/inbox', label: 'Входящие', icon: <Inbox size={18} />, module: 'M34', perm: 'party.read' },
      { to: '/calls', label: 'Звонки', icon: <Phone size={18} />, module: 'M33', perm: 'party.read' },
      { to: '/sms', label: 'SMS', icon: <MessageSquare size={18} />, module: 'M25', perm: 'party.read' },
      { to: '/bot', label: 'Чат-бот', icon: <MessagesSquare size={18} />, module: 'M22', perm: 'deal.read' },
      { to: '/app-orders', label: 'Мобильный Юрист', icon: <Smartphone size={18} />, module: 'M28', perm: 'deal.read' },
      { to: '/parties', label: 'Клиенты и контрагенты', icon: <Users size={18} />, module: 'M01', perm: 'party.read' },
      { to: '/contracts', label: 'Договоры', icon: <ScrollText size={18} />, module: 'M03', perm: 'contract.read' },
      { to: '/expenses', label: 'Затраты и расходы', icon: <Wallet size={18} />, module: 'M04', perm: 'expense.read' },
    ],
  },
  {
    title: 'Администрирование',
    items: [
      { to: '/admin/users', label: 'Пользователи и роли', icon: <Settings size={18} />, perm: 'user.manage' },
      { to: '/admin/templates', label: 'Шаблоны документов', icon: <FileCog size={18} />, module: 'M36', perm: 'document.read' },
      { to: '/admin/dictionaries', label: 'Справочники', icon: <ReceiptText size={18} />, perm: 'dictionary.write' },
      { to: '/admin/ai', label: 'ИИ и данные', icon: <Bot size={18} />, perm: 'user.manage' },
      { to: '/admin/branding', label: 'Фирменный стиль', icon: <Palette size={18} />, module: 'M27', perm: 'user.manage' },
      { to: '/admin/license', label: 'Лицензия', icon: <KeyRound size={18} />, perm: 'license.manage' },
      { to: '/admin/audit', label: 'Журнал аудита', icon: <ScrollText size={18} />, perm: 'audit.read' },
    ],
  },
];

export function Layout() {
  const { me, logout, can, hasModule } = useAuth();
  const brand = useBranding();
  const [open, setOpen] = useState(false);
  const visible = (i: Item) => (!i.module || (Array.isArray(i.module) ? i.module.some(hasModule) : hasModule(i.module))) && (!i.perm || can(i.perm));

  const nav = (
    <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
      {NAV.map((g, gi) => {
        const items = g.items.filter(visible);
        if (!items.length) return null;
        return (
          <div key={gi}>
            {g.title && <div className="mb-1 px-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">{g.title}</div>}
            {items.map((i) => (
              <NavLink key={i.to} to={i.to} end={i.to === '/'} onClick={() => setOpen(false)}
                className={({ isActive }) => clsx('flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm',
                  isActive ? 'bg-brand-50 font-medium text-brand-700' : 'text-slate-700 hover:bg-slate-100')}>
                {i.icon}{i.label}
              </NavLink>
            ))}
          </div>
        );
      })}
    </nav>
  );

  const side = (
    <div className="flex h-full w-64 flex-col border-r border-slate-200 bg-white">
      <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
        <img src={brand.logo ?? '/favicon.svg'} alt="" className="h-7 w-7 object-contain" />
        <div className="leading-tight">
          <div className="text-sm font-semibold">{brand.name}</div>
          <NavLink to="/about" className="block text-[11px] text-slate-500 hover:underline">версия 8.0 · О программе</NavLink>
        </div>
      </div>
      {nav}
      <div className="border-t border-slate-200 p-3">
        <NavLink to="/help" onClick={() => setOpen(false)} className="flex items-center gap-2 rounded px-2 py-1 text-sm text-slate-600 hover:bg-slate-100"><BookOpen size={16} /> База знаний</NavLink>
        <button onClick={() => { setOpen(false); startTour(); }} className="flex w-full items-center gap-2 rounded px-2 py-1 text-sm text-slate-600 hover:bg-slate-100"><Compass size={16} /> Тур по CRM</button>
        <NavLink to="/profile" className="mt-1 block truncate rounded px-2 py-1 text-sm hover:bg-slate-100">{me?.fullName}</NavLink>
        <button onClick={logout} className="mt-1 flex w-full items-center gap-2 rounded px-2 py-1 text-sm text-slate-600 hover:bg-slate-100">
          <LogOut size={16} /> Выйти
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen">
      <aside className="hidden lg:block">{side}</aside>
      {open && (
        <div className="fixed inset-0 z-40 flex lg:hidden">
          <div className="relative">{side}<button className="absolute right-2 top-3 p-1" onClick={() => setOpen(false)} aria-label="Закрыть меню"><X size={18} /></button></div>
          <div className="flex-1 bg-slate-900/40" onClick={() => setOpen(false)} />
        </div>
      )}
      <main className="flex-1 overflow-y-auto">
        <div className="sticky top-0 z-30 flex items-center gap-2 border-b border-slate-200 bg-white px-4 py-2 lg:hidden">
          <button onClick={() => setOpen(true)} aria-label="Меню"><Menu size={20} /></button>
          <span className="flex-1 text-sm font-semibold">{brand.name}</span>
          {hasModule('M26') && <NavLink to="/m" className="text-xs text-brand-700" onClick={(e) => { e.preventDefault(); location.href = '/m'; }}>Мобильная версия</NavLink>}
        </div>
        <div className="mx-auto max-w-7xl p-4 lg:p-6"><Outlet /></div>
      </main>
      <Tour />
      {hasModule('M33') && can('party.read') && <IncomingCallPopup />}
    </div>
  );
}
